import { AvatarDropdown, AvatarName, Question, SelectLang, ThemeToggle } from '@/components';
import { LinkOutlined } from '@ant-design/icons';
import { type Settings as LayoutSettings } from '@ant-design/pro-components';
import { Link, type RunTimeLayoutConfig } from '@umijs/max';
import React, { useState } from 'react';
import defaultSettings from '../config/defaultSettings';
import keycloak from '@/services/keycloak';
import { validateGuid } from '@/services/wfx';
import { getRuntimeConfig, isHideMenu, isWfxAuthEnabled } from '@/utils/runtimeConfig';
import {
  getWfxSession,
  mapSessionToCurrentUser,
  readGuidFromUrl,
  setWfxSession,
  stripGuidFromUrl,
} from '@/utils/wfxSession';
import SessionGuard from '@/wrappers/SessionGuard';
import { errorConfig } from './requestErrorConfig';
import './theme/design-tokens.css';


const isDev = process.env.NODE_ENV === 'development';

const STUB_USER = {
  name: 'John Doe',
  avatar: '',
  permissions: [] as any[],
};

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
}> {
  if (!isWfxAuthEnabled()) {
    return {
      currentUser: STUB_USER,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  let session = getWfxSession();
  if (!session) {
    const guid = readGuidFromUrl();
    if (guid) {
      session = await validateGuid(guid);
      if (session) {
        setWfxSession(session);
        stripGuidFromUrl();
      }
    }
  }

  return {
    currentUser: session ? mapSessionToCurrentUser(session) : undefined,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }: any) => {
  const [collapsed, setCollapsed] = useState(false);
  const hideMenu = isHideMenu();

  return {
    siderWidth: 200,
    breadcrumbRender: false,
    settingRender: false,
    collapsed,
    onCollapse: setCollapsed,
    menuRender: hideMenu ? false : undefined,
    headerRender: hideMenu ? false : undefined,
    onPageChange: async () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    },
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
      <ThemeToggle key="theme" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: any, avatarChildren: any) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    bgLayoutImgList: [
      {
        src: '/bg-1.webp',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: '/bg-2.webp',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: '/bg-3.webp',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI Document</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return <SessionGuard>{children}</SessionGuard>;
    },
    ...initialState?.settings,
  };
};

export const request = {
  ...errorConfig,
  requestInterceptors: [
    (url: string, options: any) => {
      const isFullUrl = /^http(s)?:\/\//.test(url);
      const baseUrl = getRuntimeConfig().API_URL || '';
      const finalUrl = isFullUrl ? url : `${baseUrl}${url}`;
      const token = keycloak.token;
      return {
        url: finalUrl,
        options: {
          ...options,
          headers: {
            ...options.headers,
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        },
      };
    },
  ],
};
